import React from "react";
import { Link, matchPath, useLocation, useParams } from "react-router";
import { useClientRoutesAndLabels } from "../../ui/routes";

interface Breadcrumb {
  path: string;
  label?: string;
  url?: string;
  isExact?: boolean;
  notLinkable?: boolean;
}

const BreadCrumbs: React.FC = () => {
  const { client, id, hcpID, state } = useParams<{
    client: string;
    id: string | undefined;
    hcpID: string | undefined;
    state: string | undefined;
  }>();

  const routes = useClientRoutesAndLabels(client);

  const location = useLocation();

  if (location === undefined) {
    return null;
  }

  //Translate paths into nice names for use in the breadcrumb trail
  //Note that the order here matters
  const breadcrumbNameMap: Breadcrumb[] = [
    {
      path: routes.client.dpp.with({ client }),
      label: routes.client.dpp.label,
      notLinkable: true,
    },
    { path: routes.client.dpp.payers.with({ client }), label: routes.client.dpp.payers.label },
    { path: routes.client.dpp.payers.show.with({ client, id }), label: routes.client.dpp.payers.show.label },
    {
      path: routes.client.dpp.payers.show.providers.with({ client, id }),
      label: routes.client.dpp.payers.show.providers.label,
    },
    {
      path: routes.client.dpp.payers.show.pinned.providers.with({ client, id, hcpID }),
      label: routes.client.dpp.payers.show.providers.label,
    },
    { path: routes.client.dpp.geo.with({ client }), label: routes.client.dpp.geo.label },
    { path: routes.client.dpp.geo.show.with({ client, state }), label: routes.client.dpp.geo.show.label },
    { path: routes.client.dpp.hcps.with({ client }), label: routes.client.dpp.hcps.label },
    { path: routes.client.dpp.hcps.show.with({ client, id }), label: routes.client.dpp.hcps.show.label },
    { path: routes.client.print.with({ client }), label: routes.client.print.label },
    { path: routes.client.print.messages.with({ client }), label: routes.client.print.messages.label },
    { path: routes.client.print.email.with({ client }), label: routes.client.print.email.label },
    { path: routes.client.print.resources.with({ client }), label: routes.client.print.resources.label },
    { path: routes.client.print.queue.with({ client }), label: routes.client.print.queue.label },
    { path: routes.client.profile.with({ client }), label: routes.client.profile.label },
  ];

  /* Check if the current path matches each section of our breadcrumbMap,
  and if it does, grab the translated friendly name and add it to an array */
  const generateBreadcrumbArray: () => Breadcrumb[] = () => {
    const breadCrumbArray: Breadcrumb[] = [];
    breadcrumbNameMap.forEach((item) => {
      const match = matchPath({ path: item.path, end: false }, location.pathname);
      if (match) {
        breadCrumbArray.push({
          path: item.path,
          label: item.label,
          url: item.path,
          isExact: match.pathname === location.pathname,
          notLinkable: item.notLinkable,
        });
      }
    });
    return breadCrumbArray;
  };

  const breadcrumbsArray = generateBreadcrumbArray();

  return (
    <ul className="hidden md:flex space-x-4 pb-6 breadcrumb-container">
      {breadcrumbsArray.map((breadcrumb) => {
        const activeClass = breadcrumb.isExact ? "text-brand underline" : "";
        return (
          <li key={breadcrumb.path}>
            {breadcrumb.notLinkable ? (
              <span className={`text-slate-gray focus:ring-2 body-2 ${activeClass}`}>{breadcrumb.label}</span>
            ) : (
              <Link className={`text-slate-gray focus:ring-2 body-2 ${activeClass}`} replace to={breadcrumb.path}>
                {breadcrumb.label}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default BreadCrumbs;
